/**
 * Capture — turning a saved post into something cookable.
 *
 * The screen's job is as much expectation-setting as it is input. It says what
 * each network will and will not give up BEFORE she waits on a request, because
 * the alternative is a spinner followed by a disappointment, and after two of
 * those she stops trusting the feature.
 */
import { useEffect, useState } from 'react'
import Icon from '../Icon'
import {
  captureLink, captureScreenshot, captureText, capturePlatforms, MAX_CAPTURE_IMAGES,
  type CaptureImage, type CaptureResult, type PlatformInfo, type ScreenshotRead,
} from '../../lib/savenmake'

type Mode = 'link' | 'text' | 'shot' | 'manual'

export default function Import({ onImported, onOpenRecipe, onCook }: {
  onImported: () => void
  onOpenRecipe: (id: string) => void
  onCook: (recipeId: string) => void
}) {
  const [mode, setMode] = useState<Mode>('link')
  const [platforms, setPlatforms] = useState<Record<string, PlatformInfo>>({})
  const [url, setUrl] = useState('')
  const [text, setText] = useState('')
  const [title, setTitle] = useState('')
  /* The pictures she has chosen but not yet sent, in the order they will be
     read. A caption long enough to hold a recipe does not fit on one screen. */
  const [files, setFiles] = useState<File[]>([])
  const [previews, setPreviews] = useState<string[]>([])
  const [shotId, setShotId] = useState<string | null>(null)
  const [shotRead, setShotRead] = useState<ScreenshotRead | null>(null)
  /* Every picture on the capture, as the server has them. */
  const [images, setImages] = useState<CaptureImage[]>([])
  /* Links found across ALL of them — a caption's link is as likely to be in
     the third screenshot as the first. */
  const [urlsFound, setUrlsFound] = useState<string[]>([])
  const [reading, setReading] = useState<{ done: number; total: number } | null>(null)
  const [busy, setBusy] = useState(false)
  /*
   * What is happening, in her words, not a spinner.
   *
   * Following a link found in a screenshot takes several seconds — we fetch the
   * page, look for structured data, parse it, and build the recipe. With only a
   * disabled button to look at, that reads as "nothing happened", and the second
   * tap is how you get two of everything.
   */
  const [step, setStep] = useState<string | null>(null)
  const [result, setResult] = useState<CaptureResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { void capturePlatforms().then(setPlatforms).catch(() => {}) }, [])

  /*
   * Share-target arrival.
   *
   * An installed app on Android can be a share destination, and the manifest
   * routes that share to /?shared=<url>. Nothing to configure and nothing to
   * promise on iOS, where the share sheet cannot do this — the copy elsewhere
   * says paste instead.
   */
  useEffect(() => {
    const shared = new URLSearchParams(window.location.search).get('shared')
    if (shared) {
      setUrl(shared)
      setMode('link')
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [])

  /*
   * Thumbnails for the pictures she has chosen but not yet sent.
   *
   * Revoked when the selection changes rather than left to the page unload. She
   * picks, reorders and discards several times before uploading, and every
   * unrevoked object URL pins its whole image in memory — on a phone, with
   * eight screenshots, that is the tab being killed.
   */
  useEffect(() => {
    const made = files.map((f) => URL.createObjectURL(f))
    setPreviews(made)
    return () => { for (const u of made) URL.revokeObjectURL(u) }
  }, [files])

  const detected = detectPlatform(url)
  const info = detected ? platforms[detected] : undefined
  const unread = images.filter((im) => !im.read)

  /**
   * Send the chosen pictures, one at a time, in her order.
   *
   * Sequential rather than one request carrying all of them, for three reasons
   * that all point the same way: the screen can say which picture it is on
   * instead of showing a spinner for a minute; a failure on the third KEEPS the
   * first two, each of which cost a metered model call; and no single request
   * runs long enough for a phone browser to give up on it.
   *
   * `appendTo` is the capture already under way, so a picture added later joins
   * the same capture rather than starting a rival one.
   */
  const readShots = async (pending: File[], appendTo: string | null) => {
    if (!pending.length) return
    setBusy(true); setError(null)
    let captureId = appendTo
    let done = 0
    try {
      for (const [i, f] of pending.entries()) {
        setReading({ done: i, total: pending.length })
        const read = await captureScreenshot(f, url.trim() || undefined, captureId ?? undefined)
        captureId = read.id
        setShotId(read.id)
        setShotRead(read)
        setImages(read.images)
        setUrlsFound((prev) => [...new Set([...prev, ...read.urlsFound])])
        /* Appended, never assigned. By the time a later picture arrives she may
           have corrected the earlier ones, and replacing the box would throw
           all of that away. */
        const words = read.transcription
        if (words) setText((prev) => (prev.trim() ? `${prev.trimEnd()}\n\n${words}` : words))
        done++
      }
      setFiles([])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed.')
      /* What was read before the failure is kept, and what was not stays in the
         queue so she can try those again without re-picking the lot. */
      setFiles(pending.slice(done))
    } finally { setBusy(false); setReading(null) }
  }

  const run = async (fn: () => Promise<CaptureResult>, doing?: string) => {
    setBusy(true); setError(null); setResult(null); setStep(doing ?? null)
    try {
      const r = await fn()
      setResult(r)
      if (r.recipeId) onImported()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work.')
    } finally { setBusy(false); setStep(null) }
  }

  /**
   * Choosing the pictures, putting them in order, and sending them.
   *
   * One element rather than two, rendered wherever she is standing: above the
   * results before anything has been read, and below the pictures afterwards.
   * The first version had the file input at the bottom of the screen and the
   * button that acts on it at the top, which is a scroll in each direction to
   * do one thing.
   */
  const picker = (
    <>
      <label style={shotId ? { marginTop: 14 } : undefined}>
        {shotId
          ? 'Another screenshot — its words get added to the end'
          : files.length ? 'More screenshots' : 'The screenshots'}
        <input
          type="file" multiple accept="image/png,image/jpeg,image/webp"
          disabled={busy}
          onChange={(e) => {
            const picked = Array.from(e.target.files ?? [])
            setFiles((prev) => [...prev, ...picked].slice(0, MAX_CAPTURE_IMAGES - images.length))
            /* Cleared so picking the same file again still fires a change, and
               so the control never shows a list she has since edited. */
            e.target.value = ''
          }}
        />
      </label>

      {/*
        * The order, set BEFORE anything is read.
        *
        * A file picker hands back a multiple selection in whatever order it
        * likes — usually by filename, which for screenshots is the order she
        * took them only if she took them in order. For a recipe split across
        * three pictures the order IS the method, so she fixes it here, while it
        * is still just a list. Doing it afterwards would mean reshuffling text
        * she had already begun correcting.
        */}
      {!!files.length && (
        <>
          <p className="small muted">
            Read in this order and added to one recipe — put them the way the caption runs.
            {' '}{files.length + images.length} of {MAX_CAPTURE_IMAGES}.
          </p>
          <ol className="shot-queue">
            {files.map((f, i) => (
              <li key={`${f.name}-${f.lastModified}-${i}`}>
                <span className="shot-queue-n">{images.length + i + 1}</span>
                {previews[i] && <img src={previews[i]} alt="" />}
                <span className="shot-queue-name">{f.name}</span>
                <button
                  type="button" aria-label={`Move ${f.name} earlier`}
                  disabled={i === 0 || busy} onClick={() => setFiles((p) => swap(p, i, i - 1))}
                >
                  <Icon name="up" size={16} />
                </button>
                <button
                  type="button" aria-label={`Move ${f.name} later`}
                  disabled={i === files.length - 1 || busy} onClick={() => setFiles((p) => swap(p, i, i + 1))}
                >
                  <Icon name="down" size={16} />
                </button>
                <button
                  type="button" aria-label={`Remove ${f.name}`}
                  disabled={busy} onClick={() => setFiles((p) => p.filter((_, n) => n !== i))}
                >
                  <Icon name="close" size={16} />
                </button>
              </li>
            ))}
          </ol>
          <button
            type="button" className="primary" disabled={busy}
            onClick={() => void readShots(files, shotId)}
          >
            {busy
              ? reading ? `Reading ${reading.done + 1} of ${reading.total}…` : 'Reading…'
              : shotId
                ? `Read ${files.length === 1 ? 'this one too' : `these ${files.length} too`}`
                : `Upload and read ${files.length === 1 ? 'it' : `all ${files.length}`}`}
          </button>
        </>
      )}
    </>
  )

  return (
    <>
      <div className="card">
        <h1 style={{ marginBottom: 4 }}>Save something</h1>
        <p className="muted" style={{ marginTop: 0 }}>Turn inspiration into something delicious.</p>

        <div className="import-choice">
          <button type="button" aria-current={mode === 'link'} onClick={() => setMode('link')}>
            <b><Icon name="link" size={16} /> Paste a link</b>
            <span>A reel, a pin, a blog post</span>
          </button>
          <button type="button" aria-current={mode === 'text'} onClick={() => setMode('text')}>
            <b><Icon name="text" size={16} /> Paste the caption</b>
            <span>Works everywhere. Most reliable.</span>
          </button>
          <button type="button" aria-current={mode === 'shot'} onClick={() => setMode('shot')}>
            <b><Icon name="camera" size={16} /> Screenshot</b>
            <span>Keep the picture, type the recipe</span>
          </button>
        </div>

        {mode === 'link' && (
          <>
            <label>
              The link
              <input
                type="url" value={url} onChange={(e) => setUrl(e.target.value)}
                placeholder="https://…" inputMode="url" autoCapitalize="off" autoCorrect="off"
              />
            </label>
            {info && (
              <div className="platform-note">
                <Icon name={info.fetchable ? 'info' : 'alert'} size={18} />
                <span><b>{info.label}.</b> {info.note}</span>
              </div>
            )}
            <button
              type="button" className="primary" style={{ marginTop: 16 }}
              disabled={!url.trim() || busy}
              onClick={() => run(() => captureLink(url.trim()), `Opening ${hostOf(url)}…`)}
            >
              {busy ? 'Having a look…' : 'Save this link'}
            </button>
          </>
        )}

        {mode === 'text' && (
          <>
            <p className="small muted">
              Long-press the caption, copy it, paste it here. This is the surest route on
              Instagram and Facebook, which do not let an app read a post from its link.
              We first remove unrelated text, then build the recipe from what remains.
            </p>
            <label>
              Where it came from — optional, but it is how you reopen the video
              <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" inputMode="url" autoCapitalize="off" />
            </label>
            <label>
              A name for it — optional
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Courtney's focaccia" />
            </label>
            <label>
              The recipe
              <textarea
                value={text} onChange={(e) => setText(e.target.value)} rows={12}
                placeholder={'Ingredients:\n- 2 cups flour\n- 1 tsp salt\n\nInstructions:\n1. Mix everything.\n2. Bake at 400F for 25 minutes until golden.'}
              />
            </label>
            <button
              type="button" className="primary"
              disabled={text.trim().length < 20 || busy}
              onClick={() => run(() => captureText(text, url.trim() || undefined, title.trim() || undefined), 'Reading the recipe…')}
            >
              {busy ? 'Reading it…' : 'Read this into a recipe'}
            </button>
          </>
        )}

        {mode === 'shot' && (
          <>
            <div className="platform-note">
              <Icon name="info" size={18} />
              <span>
                <b>Screenshot the caption and drop it here.</b> More than one is fine — a long caption
                takes two or three. They get read for you, in the order you put them, then you check
                them against the pictures before saving. Nothing is saved until you say so.
              </span>
            </div>
            {!shotId && picker}

            {shotId && (
              <>
                {/*
                  * The best outcome, offered first.
                  *
                  * A link in a caption is the one thing she cannot copy out of
                  * the Instagram app by hand, and following it reaches the
                  * structured path — which is exact, rather than a
                  * transcription to be checked. Gathered across every picture,
                  * because the link is as likely to be in the last as the first.
                  */}
                {!!urlsFound.length && (
                  <div className="outcome ok">
                    <h3><Icon name="link" size={18} /> There&rsquo;s a link in {images.length > 1 ? 'those pictures' : 'that picture'}</h3>
                    <p>Following it usually gives a far better result than reading the caption.</p>
                    {urlsFound.map((u) => (
                      <div className="filter-row" key={u} style={{ marginBottom: 8 }}>
                        <code style={{ flex: '1 1 200px', wordBreak: 'break-all' }}>{u}</code>
                        <button
                          type="button" className="primary" style={{ width: 'auto' }}
                          disabled={busy}
                          /* Exactly what pasting the link does — same endpoint,
                             same parsing, same duplicate handling. Nothing about
                             this path is a second implementation. */
                          onClick={() => run(() => captureLink(u), `Opening ${hostOf(u)}…`)}
                        >
                          {busy ? 'Working…' : 'Try this link'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/*
                  * Which ones could not be read, by number.
                  *
                  * "That screenshot could not be read" is no use when there were
                  * three of them: she cannot tell which of her pictures is
                  * missing from the box, so she cannot know what to type. The
                  * count and the number are the whole message.
                  */}
                {unread.length > 0 && (
                  <p className="warn">
                    {unread.length === images.length
                      ? images.length === 1
                        ? images[0]?.reason ?? 'That screenshot could not be read. Type what it says instead.'
                        : 'None of those could be read. Type what they say instead.'
                      : `${unread.length === 1 ? 'Picture' : 'Pictures'} ${listNumbers(unread.map((im) => im.position + 1))} could not be read — what ${unread.length === 1 ? 'it says is' : 'they say is'} not in the box below. Type ${unread.length === 1 ? 'it' : 'them'} in.`}
                  </p>
                )}

                <div className="editor-grid two">
                  <div>
                    <label style={{ marginBottom: 6 }}>
                      {images.length > 1 ? `The pictures — ${images.length}, in order` : 'The picture'}
                    </label>
                    <div className="shot-stack">
                      {images.map((im) => (
                        <figure key={im.position}>
                          <img className="shot" src={im.url} alt={`Screenshot ${im.position + 1} of ${images.length}`} />
                          <figcaption className={im.read ? 'muted small' : 'warn small'}>
                            {images.length > 1 && <b>{im.position + 1}. </b>}
                            {im.read ? 'Read into the box' : im.reason ?? 'Could not be read'}
                          </figcaption>
                        </figure>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label>
                      {text ? 'What we read — check it against the pictures' : 'Type what they say'}
                      <textarea
                        value={text} onChange={(e) => setText(e.target.value)} rows={16}
                        disabled={busy} placeholder="Ingredients:&#10;…"
                      />
                    </label>
                    {shotRead?.transcription && (
                      /* Said out loud. A vision model generates text conditioned
                         on a picture; it does not scan one. Trusting it silently
                         is how invented ingredients get into a recipe. */
                      <p className="muted small">
                        Read automatically, so it can be wrong. Fix anything that does not match before saving.
                        We then remove unrelated text before building the recipe.
                      </p>
                    )}
                    <button
                      type="button" className="primary"
                      disabled={text.trim().length < 20 || busy}
                      onClick={() => run(() => captureText(text, url.trim() || undefined, title.trim() || undefined), 'Saving the recipe…')}
                    >
                      {busy ? 'Saving…' : 'Save this as a recipe'}
                    </button>
                  </div>
                </div>

                {/*
                  * Another picture, after the fact.
                  *
                  * She reads two, starts correcting the text, and realises the
                  * method was on a third. A new picture's words are APPENDED to
                  * whatever is in the box — never a replacement, which would
                  * throw away every correction she had made so far.
                  */}
                {images.length < MAX_CAPTURE_IMAGES && picker}
              </>
            )}
          </>
        )}
        {error && <p className="error" style={{ marginTop: 14 }}>{error}</p>}
      </div>

      {/* Started. Visible, named, and impossible to mistake for nothing. */}
      {busy && step && (
        <div className="outcome" aria-live="polite">
          <h3><Icon name="clock" size={18} /> {step}</h3>
          <p className="muted small" style={{ marginBottom: 0 }}>This can take a few seconds. Leave it be.</p>
        </div>
      )}

      {/* Finished. */}
      {result && (
        <Outcome
          result={result}
          onOpenRecipe={onOpenRecipe}
          onCook={onCook}
          onSwitchToText={() => { setMode('text'); setResult(null) }}
          /* Her words are still in `text`; just put her back on the box. */
          onFixHere={result.gap && text.trim() ? () => {
            setMode(mode === 'shot' ? 'shot' : 'text')
            setResult(null)
            requestAnimationFrame(() => {
              document.querySelector('textarea')?.focus()
              document.querySelector('textarea')?.scrollIntoView({ block: 'center', behavior: 'smooth' })
            })
          } : undefined}
          onSaveAnyway={result.gap && text.trim() ? () => run(
            () => captureText(text, url.trim() || undefined, title.trim() || undefined, true),
            'Saving what we have…',
          ) : undefined}
        />
      )}
    </>
  )
}

/**
 * The result, told honestly.
 *
 * Four different things can have happened and they get four different
 * messages. The one that matters most is `insufficient`/`unreachable`: the link
 * IS saved, and that is presented as a real outcome with a next step rather
 * than as a failure — but it is never called an import.
 */
function Outcome({ result, onOpenRecipe, onCook, onSwitchToText, onFixHere, onSaveAnyway }: {
  result: CaptureResult
  onOpenRecipe: (id: string) => void
  onCook: (recipeId: string) => void
  onSwitchToText: () => void
  /** Scroll back to the text box with her words still in it. */
  onFixHere?: () => void
  /** Keep the half-read recipe and finish it in the editor. */
  onSaveAnyway?: () => void
}) {
  if (result.savedPartial && result.recipeId) {
    return (
      <div className="outcome partial">
        <h3><Icon name="check" size={20} /> Saved, with a note about what is missing</h3>
        <p>{result.reason} It is in your library with a reminder attached — finish it whenever you like.</p>
        <button type="button" className="primary" style={{ width: 'auto' }} onClick={() => onOpenRecipe(result.recipeId!)}>
          Finish it now
        </button>
      </div>
    )
  }

  if (result.status === 'parsed' && result.recipeId) {
    return (
      <div className="outcome ok">
        <h3><Icon name="check" size={20} /> Read it — {result.parsed?.ingredients.length} ingredients, {result.parsed?.steps.length} steps</h3>
        {result.parsed?.cleanedText && (
          <details>
            <summary>Recipe text used</summary>
            <p style={{ whiteSpace: 'pre-wrap' }}>{result.parsed.cleanedText}</p>
          </details>
        )}
        <p>Saved to your library. Have a look and correct anything the post was vague about — nothing is marked as yours until you touch it.</p>
        <div className="filter-row">
          <button type="button" className="primary" style={{ width: 'auto' }} onClick={() => onOpenRecipe(result.recipeId!)}>
            Review it
          </button>
          {/* Straight into cooking, because the whole point is a recipe made. */}
          <button type="button" className="accent" onClick={() => onCook(result.recipeId!)}>
            <Icon name="flame" size={16} /> Begin recipe
          </button>
        </div>
      </div>
    )
  }

  if (result.status === 'duplicate') {
    return (
      <div className="outcome">
        <h3><Icon name="info" size={20} /> You already have this one</h3>
        <p>Saved earlier — opening it rather than making a second copy.</p>
        {result.existingRecipeId && (
          <button type="button" className="primary" style={{ width: 'auto' }} onClick={() => onOpenRecipe(result.existingRecipeId!)}>
            Open it
          </button>
        )}
      </div>
    )
  }

  /*
   * The dead end, made not-dead.
   *
   * The old version said "not enough there to cook from" and offered to switch
   * modes — which cleared everything and led straight back here. Courtney hit
   * that loop. Three things fix it: say WHICH half is missing, keep her text on
   * screen so she can add the missing half without retyping the rest, and let
   * her save it half-finished if she would rather do it later.
   */
  return (
    <div className="outcome partial">
      <h3><Icon name="alert" size={20} /> {result.gap ? gapTitle(result.gap) : 'Not enough there to cook from'}</h3>
      <p>{result.reason}</p>
      {result.gap && <p><strong>{result.gap.todo}</strong></p>}

      {result.gap && (
        <div className="found-counts">
          <span className={result.gap.found.ingredients ? 'has' : 'lacks'}>
            <Icon name={result.gap.found.ingredients ? 'check' : 'close'} size={14} />
            {result.gap.found.ingredients} ingredients
          </span>
          <span className={result.gap.found.steps ? 'has' : 'lacks'}>
            <Icon name={result.gap.found.steps ? 'check' : 'close'} size={14} />
            {result.gap.found.steps} steps
          </span>
        </div>
      )}

      {result.url && (
        <p className="small"><b>The link is saved either way</b> — {result.url}</p>
      )}

      <div className="filter-row">
        {onFixHere && (
          <button type="button" className="primary" style={{ width: 'auto' }} onClick={onFixHere}>
            <Icon name="edit" size={16} /> Add what&rsquo;s missing
          </button>
        )}
        {/* The definitive way out. Keeps what was read, flags the rest, and she
            finishes it in the editor whenever she likes. */}
        {onSaveAnyway && (
          <button type="button" onClick={onSaveAnyway}>
            Save it anyway, I&rsquo;ll finish it later
          </button>
        )}
        {!onFixHere && (
          <button type="button" className="primary" style={{ width: 'auto' }} onClick={onSwitchToText}>
            Paste the caption instead
          </button>
        )}
      </div>
    </div>
  )
}

function gapTitle(gap: { missing: string }): string {
  if (gap.missing === 'steps') return 'The ingredients are there, but not the method'
  if (gap.missing === 'ingredients') return 'The method is there, but not the ingredients'
  return 'Nothing readable in that yet'
}

/** Move one item, without mutating the array React is rendering from. */
function swap<T>(items: T[], from: number, to: number): T[] {
  if (to < 0 || to >= items.length) return items
  const next = [...items]
  const moved = next.splice(from, 1)
  next.splice(to, 0, ...moved)
  return next
}

/** "2", "2 and 4", "2, 4 and 5" — a list a person reads, not an array. */
function listNumbers(numbers: number[]): string {
  if (numbers.length <= 1) return String(numbers[0] ?? '')
  return `${numbers.slice(0, -1).join(', ')} and ${numbers[numbers.length - 1]}`
}

/** Just the host, for a status line she can read. */
function hostOf(raw: string): string {
  try { return new URL(raw.trim()).hostname.replace(/^www\./, '') } catch { return 'the link' }
}

function detectPlatform(raw: string): string | null {
  try {
    const host = new URL(raw.trim()).hostname.toLowerCase().replace(/^(www|m)\./, '')
    if (host.includes('instagram')) return 'instagram'
    if (host.includes('tiktok')) return 'tiktok'
    if (host.includes('facebook') || host.includes('fb.watch')) return 'facebook'
    if (host.includes('pinterest') || host.includes('pin.it')) return 'pinterest'
    if (host.includes('youtu')) return 'youtube'
    return 'web'
  } catch { return null }
}
